import { CommonModule } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { DashboardApiService } from '../../services/dashboard-api.service';
import { ProductsApiService } from '../../services/products-api.service';
import { LiveUpdatesService } from '../../services/live-updates.service';
import { CamelToTitlePipe } from '../../shared/camel-to-title.pipe';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { formatDdMmYyyy } from '../../shared/date-format.helper';
import { reportApiError } from '../../shared/api-result.helper';

interface Alert {
  type: string;
  message: string;
  productName?: string;
  stepName?: string;
  machineTypeName?: string;
  machineName?: string | null;
  reasonName?: string;
  note?: string;
  operatorName?: string;
  createdAt?: string;
}

// Only used by the "Activity" card, which is commented out below — kept for easy re-enable.
// interface ActivityItem {
//   time: string;
//   mode: string;
//   productName?: string;
//   stepName?: string;
// }

interface ProductOption {
  _id: string;
  name: string;
}

type ChartKind = 'bar' | 'line' | 'doughnut' | 'pie';

// Bar charts use one color per dataset (categories are told apart by the x-axis instead), but
// pie/doughnut charts have no axis — each slice needs its own color to be readable. This palette
// is cycled through per-slice only when a chart is switched to pie/doughnut.
const CHART_SLICE_PALETTE = [
  '#3ac47d', '#f7b924', '#3f6ad8', '#d92550', '#8965e0',
  '#2c9faf', '#f4772e', '#00b8d4', '#6c5ce7', '#ff6b81',
];

const todayDateString = () => new Date().toISOString().slice(0, 10);
const currentMonthString = () => todayDateString().slice(0, 7);
const firstDayOfMonthString = () => `${currentMonthString()}-01`;

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, BaseChartDirective, CamelToTitlePipe, AppendToBodyDirective],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  target = 0;
  completed = 0;
  pending = 0;
  efficiencyPct = 0;
  stepActions = 0;
  alerts: Alert[] = [];
  perOperator: { operatorId: string; name: string; count: number }[] = [];
  stepWise: { productId: string; productName: string; stepId: string; stepName: string; count: number }[] = [];
  operatorStepWise: { operatorId: string; operatorName: string; stepId: string; stepName: string; count: number }[] = [];
  // activityFeed: ActivityItem[] = []; // "Activity" card is commented out below
  loading = true;

  products: ProductOption[] = [];

  // Filter form (edited freely; only takes effect once Submit is pressed — see appliedFilter).
  filterForm = {
    productId: '',
    periodType: 'day' as 'month' | 'day',
    startMonth: currentMonthString(),
    endMonth: currentMonthString(),
    startDate: firstDayOfMonthString(),
    endDate: todayDateString(),
  };

  // The range/product/mode actually driving the KPIs, table and charts right now.
  appliedFilter = { startDate: '', endDate: '', productId: '' as string | null, periodType: 'day' as 'month' | 'day' };
  isSingleDay = true;

  activityChartTitle = 'Hourly Activity';
  activityChartType: ChartKind = 'bar';
  stepChartType: ChartKind = 'bar';
  stepDetailChartType: ChartKind = 'bar';

  get activityChartOptions(): ChartConfiguration['options'] {
    if (this.activityChartType !== 'bar') return { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } } };
    return { responsive: true, maintainAspectRatio: false, scales: { x: { stacked: true }, y: { stacked: true } } };
  }

  get stepChartOptions(): ChartConfiguration['options'] {
    if (this.stepChartType !== 'bar') return { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } } };
    return { responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } } };
  }

  get stepDetailChartOptions(): ChartConfiguration['options'] {
    if (this.stepDetailChartType !== 'bar') return { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true } } };
    return { responsive: true, maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } } };
  }

  // Swaps in a per-slice color palette when rendering as pie/doughnut; bar/line charts keep their
  // original single flat color per dataset (line especially needs one consistent color per line,
  // not one per point).
  private renderDataFor(type: ChartKind, source: ChartData<any>): ChartData<any> {
    if (type === 'bar' || type === 'line') return source;
    const sliceCount = source.labels?.length || 0;
    const colors = Array.from({ length: sliceCount }, (_, i) => CHART_SLICE_PALETTE[i % CHART_SLICE_PALETTE.length]);
    return { labels: source.labels, datasets: source.datasets.map((ds) => ({ ...ds, backgroundColor: colors })) };
  }

  // Plain fields, not getters — renderDataFor() builds a brand-new object (new arrays, new
  // per-slice colors) for pie/doughnut every time it runs, and a getter re-runs on every single
  // Angular change-detection cycle (any click anywhere on the page, not just interaction with
  // this chart). ng2-charts treats a new [data] reference as new data and redraws, so a getter
  // here made the pie/doughnut charts flicker/reset on completely unrelated clicks. Recomputed
  // explicitly instead, only when the underlying data or the selected chart type actually change.
  activityChartRenderData: ChartData<any> = { labels: [], datasets: [] };
  stepChartRenderData: ChartData<any> = { labels: [], datasets: [] };
  stepDetailChartRenderData: ChartData<any> = { labels: [], datasets: [] };

  refreshActivityChartRenderData() {
    this.activityChartRenderData = this.renderDataFor(this.activityChartType, this.activityChartData);
  }

  refreshStepChartRenderData() {
    this.stepChartRenderData = this.renderDataFor(this.stepChartType, this.stepChartData);
  }

  refreshStepDetailChartRenderData() {
    this.stepDetailChartRenderData = this.renderDataFor(this.stepDetailChartType, this.stepDetailChartData);
  }

  // Loosely typed (not ChartData<'bar'>) — this chart switches between bar and line depending on
  // the period filter, and line-specific dataset options (tension, borderDash) aren't part of
  // ChartDataset<'bar'>'s shape.
  activityChartData: ChartData<any> = { labels: [], datasets: [{ data: [], label: 'Completed', backgroundColor: '#3ac47d' }] };
  stepChartData: ChartData<'bar'> = { labels: [], datasets: [{ data: [], label: 'Completed', backgroundColor: '#f7b924' }] };
  // operatorStepChartData: ChartData<'bar'> = { labels: [], datasets: [{ data: [], label: 'Completed', backgroundColor: '#6c5ce7' }] }; // "Step-wise Completion (per Operator)" card is commented out below

  // Step-detail modal — opened by clicking a bar in "Step-wise Completion (per Product)".
  selectedStepDetail: { productName: string; stepName: string } | null = null;
  stepDetailChartData: ChartData<'bar'> = { labels: [], datasets: [{ data: [], label: 'Completed', backgroundColor: '#6c5ce7' }] };

  private toastr = inject(ToastrService);
  private unsubscribeLiveUpdates?: () => void;

  constructor(
    private dashboardApi: DashboardApiService,
    private productsApi: ProductsApiService,
    private liveUpdates: LiveUpdatesService,
    private ngZone: NgZone,
  ) {}

  async ngOnInit() {
    // The Product filter always has one product selected (no "All Products" option), so the
    // first load has to wait for the product list before it knows what to default to.
    await this.loadProducts();
    if (!this.filterForm.productId && this.products.length > 0) {
      this.filterForm.productId = this.products[0]._id;
    }
    this.applyFilter();
    this.unsubscribeLiveUpdates = this.liveUpdates.connect({
      // The push payload is always today's single-day summary — only trust it directly to
      // refresh the view when today actually falls inside the range currently being viewed;
      // otherwise re-fetch properly scoped data for whatever range/product is selected.
      'production:update': (data: any) => {
        const today = todayDateString();
        if (this.appliedFilter.startDate <= today && today <= this.appliedFilter.endDate) {
          this.load();
        }
        // "Activity" card is commented out below — feed no longer tracked.
        // this.activityFeed = [
        //   { time: new Date().toLocaleTimeString(), mode: data.entry.mode, productName: data.entry.productName, stepName: data.entry.stepName },
        //   ...this.activityFeed,
        // ].slice(0, 20);
      },
      // The periodic broadcast is always unfiltered (all products — it's a global push, not
      // scoped to any one viewer's filter), so applying it directly would blow away whatever
      // product-specific alert view is currently on screen. Re-fetch through the properly scoped
      // endpoint instead, same as the production:update handler above.
      'alert:new': () => this.load(),
    });
  }

  ngOnDestroy() {
    this.unsubscribeLiveUpdates?.();
  }

  async loadProducts() {
    const res = await this.productsApi.list();
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.products = res.Data;
      } else {
        reportApiError(this.toastr, res);
      }
    });
  }

  applyFilter() {
    const { periodType, startMonth, endMonth, startDate, endDate, productId } = this.filterForm;
    if (periodType === 'month') {
      const [sm, em] = startMonth <= endMonth ? [startMonth, endMonth] : [endMonth, startMonth];
      const [endYear, endMonthNum] = em.split('-').map(Number);
      const lastDay = new Date(endYear, endMonthNum, 0).getDate();
      this.appliedFilter = {
        startDate: `${sm}-01`,
        endDate: `${em}-${String(lastDay).padStart(2, '0')}`,
        productId: productId || null,
        periodType: 'month',
      };
    } else {
      const [from, to] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
      this.appliedFilter = { startDate: from, endDate: to, productId: productId || null, periodType: 'day' };
    }
    this.load();
  }

  async load() {
    this.loading = true;
    const res = await this.dashboardApi.getSummary({
      startDate: this.appliedFilter.startDate,
      endDate: this.appliedFilter.endDate,
      productId: this.appliedFilter.productId,
      periodType: this.appliedFilter.periodType,
    });
    // The await continuation here was resuming outside Angular's zone (the Submit button's
    // spinner would stay stuck until something else — like opening DevTools — forced a repaint,
    // even though the underlying state was already correct). Wrapping the state writes in
    // ngZone.run() guarantees a change-detection tick regardless of what zone the continuation
    // actually resumed in.
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.applySummary(res.Data);
        this.alerts = res.Data.alerts || [];
      } else {
        reportApiError(this.toastr, res);
      }
      this.loading = false;
    });
  }

  private applySummary(summary: any) {
    this.target = summary.target;
    this.completed = summary.completed;
    this.pending = summary.pending;
    this.efficiencyPct = summary.efficiencyPct;
    this.stepActions = summary.stepActions;
    this.perOperator = summary.perOperator;
    this.stepWise = summary.stepWise;
    this.operatorStepWise = summary.operatorStepWise;
    this.isSingleDay = summary.isSingleDay;

    if (summary.monthlyBuckets) {
      // Month period: one point per calendar month in the selected Start Month–End Month range,
      // with the month's target plotted alongside actual progress so over/under-pace is visible
      // at a glance instead of needing to cross-check the KPI cards separately.
      this.activityChartTitle = 'Month-wise Activity';
      this.activityChartType = 'line';
      this.activityChartData = {
        labels: summary.monthlyBuckets.map((b: any) => b.label),
        datasets: [
          { data: summary.monthlyBuckets.map((b: any) => b.finishedCount), label: 'Finished Units', backgroundColor: '#3ac47d', borderColor: '#3ac47d', tension: 0.4, order: 2 },
          { data: summary.monthlyBuckets.map((b: any) => b.inProgressCount), label: 'In Progress (steps)', backgroundColor: '#f7b924', borderColor: '#f7b924', tension: 0.4, order: 3 },
          // Target frequently lands on the exact same value as Finished Units (hitting target
          // exactly, or both sitting at 0) — a plain solid-on-dashed overlap then hides the dash
          // pattern entirely under Finished Units' line. `order: 1` forces this dataset to draw
          // last/on top of the others (Chart.js draws lower `order` values last), and a distinct
          // diamond point marker (vs. the default circle) keeps the two visually separable even
          // at a fully coincident point, not just along the line between points.
          {
            data: summary.monthlyBuckets.map((b: any) => b.target),
            label: 'Monthly Target',
            backgroundColor: '#3f6ad8',
            borderColor: '#3f6ad8',
            tension: 0.4,
            borderDash: [6, 4],
            borderWidth: 3,
            pointStyle: 'rectRot',
            pointRadius: 5,
            order: 1,
          },
        ],
      };
    } else {
      this.activityChartTitle = summary.isSingleDay ? 'Hourly Activity' : 'Daily Activity';
      this.activityChartType = summary.isSingleDay ? 'bar' : 'line';
      this.activityChartData = {
        labels: summary.activityBuckets.map((b: any) => b.label),
        datasets: [
          { data: summary.activityBuckets.map((b: any) => b.inProgressCount), label: 'In Progress (steps)', backgroundColor: '#f7b924', borderColor: '#f7b924', tension: 0.4 },
          { data: summary.activityBuckets.map((b: any) => b.finishedCount), label: 'Finished Units', backgroundColor: '#3ac47d', borderColor: '#3ac47d', tension: 0.4 },
        ],
      };
    }

    this.stepChartData = {
      labels: summary.stepWise.map((s: any) => `${s.productName} — ${s.stepName}`),
      datasets: [{ data: summary.stepWise.map((s: any) => s.count), label: 'Completed', backgroundColor: '#f7b924' }],
    };

    // "Step-wise Completion (per Operator)" card is commented out below.
    // this.operatorStepChartData = {
    //   labels: summary.operatorStepWise.map((s: any) => `${s.operatorName} — ${s.stepName}`),
    //   datasets: [{ data: summary.operatorStepWise.map((s: any) => s.count), label: 'Completed', backgroundColor: '#6c5ce7' }],
    // };

    this.refreshActivityChartRenderData();
    this.refreshStepChartRenderData();
  }

  // Chart.js gives us back the clicked bar's index into the same array stepChartData's labels
  // were built from (stepWise) — one dataset, so active[0].index maps 1:1 to stepWise[index].
  onStepChartClick(event: { event?: unknown; active?: any[] }) {
    const index = event.active?.[0]?.index;
    if (index === undefined) return;
    const step = this.stepWise[index];
    if (!step) return;

    this.selectedStepDetail = { productName: step.productName, stepName: step.stepName };
    const operatorCounts = this.operatorStepWise.filter((o) => o.stepId === step.stepId);
    this.stepDetailChartData = {
      labels: operatorCounts.map((o) => o.operatorName),
      datasets: [{ data: operatorCounts.map((o) => o.count), label: 'Completed', backgroundColor: '#6c5ce7' }],
    };
    this.refreshStepDetailChartRenderData();

    (window as any).bootstrap.Modal.getOrCreateInstance(document.getElementById('stepDetailModal')).show();
  }

  // A filtered-down selection (single product, quiet period, etc.) can legitimately have zero
  // activity — an empty chart (flat 0-1 axis, no bars) reads as "broken" rather than "nothing
  // happened here", so these gate an explicit empty-state message instead.
  get hasActivityData(): boolean {
    return this.activityChartData.datasets.some((d) => (d.data as number[]).some((v) => v > 0));
  }

  get hasStepData(): boolean {
    return ((this.stepChartData.datasets[0]?.data as number[]) || []).some((v) => v > 0);
  }

  // Surfaced on each chart's header so it's obvious at a glance which product/period the chart
  // below is actually showing, without having to scroll back up to the filter bar to check.
  get selectedProductName(): string {
    return this.products.find((p) => p._id === this.appliedFilter.productId)?.name || '';
  }

  get appliedRangeLabel(): string {
    if (!this.appliedFilter.startDate) return '';
    if (this.appliedFilter.startDate === this.appliedFilter.endDate) return formatDdMmYyyy(this.appliedFilter.startDate);
    return `${formatDdMmYyyy(this.appliedFilter.startDate)} – ${formatDdMmYyyy(this.appliedFilter.endDate)}`;
  }

  formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString();
  }
}
